/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { BoxProps } from '@mui/material';
import { Box } from '@mui/material';
import React from 'react';
import type { RowComponentProps } from 'react-window';
import { List } from 'react-window';
import WorkspaceSelectListRow, {
  WORKSPACE_SELECT_LIST_ROW_HEIGHT,
} from './WorkspaceSelectListRow';
import type {
  WorkspacePresetAction,
  WorkspacePresetListItem,
} from '../../store/workspace/types';
import { getEmptyWorkspaceListItem } from '../../store/workspaceList/utils';
import { useWorkspaceTranslation } from '../../utils/i18n';

const RowComponent = ({
  index,
  style,
  items,
  currentSelectedId,
  onClickWorkspacePreset,
  onClickWorkspacePresetOption,
  onOpenPresetInfo,
  infoOpenPresetId,
  isEditable,
  isAdmin,
  searchQuery,
  testIdPrefix,
}: RowComponentProps<{
  items: WorkspacePresetListItem[];
  currentSelectedId: string;
  onClickWorkspacePreset: (id: string) => void;
  onClickWorkspacePresetOption: (
    presetId: string,
    workspaceAction: WorkspacePresetAction,
    workspacePreset: WorkspacePresetListItem,
  ) => void;
  onOpenPresetInfo?: (preset: WorkspacePresetListItem) => void;
  infoOpenPresetId?: string;
  isEditable?: boolean;
  isAdmin?: boolean;
  searchQuery?: string;
  testIdPrefix: string;
}>): React.ReactElement => {
  const preset = items[index];
  return (
    <div
      key={preset.id || 'emptyPreset'}
      style={{
        ...style,
        paddingRight: '8px',
      }}
    >
      <WorkspaceSelectListRow
        infoOpenPresetId={infoOpenPresetId}
        isAdmin={isAdmin}
        isEditable={isEditable}
        isSelected={
          currentSelectedId === preset.id ||
          (currentSelectedId === '' && preset.id === undefined)
        }
        onClickWorkspacePreset={onClickWorkspacePreset}
        onClickWorkspacePresetOption={onClickWorkspacePresetOption}
        onOpenPresetInfo={onOpenPresetInfo}
        searchQuery={searchQuery}
        testIdPrefix={testIdPrefix}
        workspacePreset={preset}
      />
    </div>
  );
};

export interface WorkspaceSelectListProps extends BoxProps {
  workspacePresets: WorkspacePresetListItem[];
  currentSelectedId: string;
  onClickWorkspacePreset: (id: string) => void;
  onClickWorkspacePresetOption: (
    presetId: string,
    workspaceAction: WorkspacePresetAction,
    workspacePreset: WorkspacePresetListItem,
  ) => void;
  onOpenPresetInfo?: (preset: WorkspacePresetListItem) => void;
  infoOpenPresetId?: string;
  hideNewWorkspace?: boolean;
  isEditable?: boolean;
  isAdmin?: boolean;
  searchQuery?: string;
  newPreset?: WorkspacePresetListItem;
  testIdPrefix?: string; // Specifies if used for workspace or viewpreset, added to testids
}

const MIN_HEIGHT_SCROLLABLE_LIST = 88; // Slightly less than 2 rows
const LIST_PADDING_TOP = 8;

// row height = height + margin
const rowHeight = WORKSPACE_SELECT_LIST_ROW_HEIGHT + 12;

const WorkspaceSelectList: React.FC<WorkspaceSelectListProps> = ({
  workspacePresets,
  currentSelectedId,
  onClickWorkspacePreset,
  onClickWorkspacePresetOption,
  hideNewWorkspace,
  isEditable,
  isAdmin,
  searchQuery,
  newPreset,
  testIdPrefix = 'workspace',
  onOpenPresetInfo,
  infoOpenPresetId,
}: WorkspaceSelectListProps) => {
  const { t } = useWorkspaceTranslation();
  const newPresetTruthy = newPreset || getEmptyWorkspaceListItem(t);
  const items = React.useMemo(
    () =>
      !hideNewWorkspace
        ? [newPresetTruthy, ...workspacePresets]
        : workspacePresets,
    [hideNewWorkspace, newPresetTruthy, workspacePresets],
  );

  return (
    <Box
      data-testid={`${testIdPrefix}-selectList`}
      sx={{
        height: '100%',
        minHeight: MIN_HEIGHT_SCROLLABLE_LIST,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ marginBottom: '4px', fontSize: '12px', ml: 0.5 }}>
        {workspacePresets.length} {t('workspace-results')}
      </Box>
      <Box
        sx={{
          paddingTop: `${LIST_PADDING_TOP}px`,
          height: `calc(100% - 24px)`,
        }}
      >
        <List
          rowComponent={RowComponent}
          rowCount={items.length}
          rowHeight={rowHeight}
          rowProps={{
            items,
            currentSelectedId,
            onClickWorkspacePreset,
            onClickWorkspacePresetOption,
            onOpenPresetInfo,
            infoOpenPresetId,
            isEditable,
            isAdmin,
            searchQuery,
            testIdPrefix,
          }}
          // style={{
          //   height: `100%`,
          //   width: '100%',
          // }}
        />
      </Box>
    </Box>
  );
};

export default WorkspaceSelectList;
